// HU-29 (MG-ILE-57) · tarea 29-5. Administración › Maestro de productos por país.
//
// AT S3 §2.2.6 (decisión del operador 30-sep, D5): el `.xlsx` se lee EN EL NAVEGADOR y viaja una
// sola petición JSON compacta —los encabezados una vez y cada fila como arreglo, con su número de
// fila del archivo delante—, hasta 5 000 filas. Sin lotes ni confirmación y sin
// `desactivar_ausentes`: el reemplazo por país es la regla (C5, C6).
//
// Nombres del contrato: `CargaDeMaestroEntrada`, `CargaDeMaestroSalida`, `CargaDelHistorialSalida`
// y `DetalleDeCargaSalida` (`administracion/interfaces/http/esquemas.py`).

import { pedir, seg, segPais, segUuid } from './cliente';

/** Una celda tal como la acepta el contrato. */
export type ValorDeCelda = string | number | null;

export type CargaDeMaestro = {
  nombre_archivo: string;
  columnas: string[];
  /** Cada fila: `[número de fila del archivo, valor 1, valor 2, …]`. */
  filas: ValorDeCelda[][];
};

export type RechazoDeFila = {
  numero_de_fila: number;
  sku: string | null;
  motivo: string;
  /** «Fila 2 · ILD-9003 · La fila no trae nombre comercial» (HU-29-C3), armado por el servidor. */
  texto: string;
};

export type ResultadoDeCarga = {
  carga_id: string;
  filas_aceptadas: number;
  filas_rechazadas: number;
  productos_nuevos: number;
  productos_actualizados: number;
  productos_desactivados: number;
  rechazos: RechazoDeFila[];
  /** «3 aceptadas · 2 nuevas · 1 actualizada · 0 rechazadas» (HU-29-C1), tal cual. */
  texto_de_resultado: string;
};

export type CargaDelHistorial = {
  carga_id: string;
  cargado_en: string;
  cargado_por: string;
  nombre_archivo: string | null;
  filas_aceptadas: number;
  filas_rechazadas: number;
};

export type DetalleDeCarga = CargaDelHistorial & { detalle_rechazos: RechazoDeFila[] };

const RUTA = '/administracion/maestro-de-productos/cargas';

/** Endpoint 41. 201 con el resultado; 422 si falta una columna entera (C2); 413 si no cabe. */
export function cargarMaestro(carga: CargaDeMaestro) {
  return pedir<ResultadoDeCarga>(RUTA, { metodo: 'POST', cuerpo: carga });
}

/** Endpoint 44 (HU-29-C7). */
export function historialDeCargas() {
  return pedir<CargaDelHistorial[]>(RUTA);
}

/** Endpoint 45: la carga guardada con sus filas rechazadas. */
export function detalleDeCarga(cargaId: string) {
  return pedir<DetalleDeCarga>(`${RUTA}/${segUuid(cargaId)}`);
}

// ---------------------------------------------------------------------------------
// CC-02 bloque B4 (RN-73, CC2-29-C6). La ficha del producto por autoridad: una casilla por
// autoridad del país con su número de registro. Nombres del contrato: `FichaDelProductoSalida`,
// `AutoridadDeLaFichaSalida` y `RegistroAnteAutoridadEntrada` (`esquemas.py`).
// ---------------------------------------------------------------------------------

/** Una casilla de la ficha: la autoridad y el registro del producto ante ella. */
export type AutoridadDeLaFicha = {
  codigo: string;
  nombre: string;
  es_predeterminada: boolean;
  activa: boolean;
  /** «Por confirmar con ILENDER» (D-1). */
  por_confirmar: boolean;
  registrado: boolean;
  /** El último número conocido, también si el registro está desmarcado. */
  registro_sanitario: string | null;
};

export type FichaDelProducto = {
  pais: string;
  pais_nombre: string;
  sku: string;
  nombre_comercial: string;
  forma_farmaceutica: string | null;
  concentracion: string | null;
  /** El registro de respaldo de la ficha (el de la autoridad predeterminada). */
  registro_sanitario: string;
  activo: boolean;
  autoridades: AutoridadDeLaFicha[];
};

const RUTA_DEL_MAESTRO = '/administracion/maestro-de-productos';

/** La ficha de un producto en un país. 404 si no tiene ficha en ese país. */
export function fichaDelProducto(pais: string, sku: string) {
  return pedir<FichaDelProducto>(`${RUTA_DEL_MAESTRO}/${segPais(pais)}/${seg(sku)}`);
}

/**
 * Endpoint 70: marcar (con número) o desmarcar una autoridad. 422 sin número («Escribe el número
 * de registro ante SANIPES») o con una autoridad desconocida o desactivada; 409 con la ficha
 * retirada. Responde la ficha tal como queda.
 */
export function cambiarRegistroAnteAutoridad(
  pais: string, sku: string, autoridad: string, cambio: { registrado: boolean; registro_sanitario?: string | null },
) {
  return pedir<FichaDelProducto>(
    `${RUTA_DEL_MAESTRO}/${segPais(pais)}/${seg(sku)}/autoridades/${seg(autoridad)}`,
    { metodo: 'PUT', cuerpo: cambio },
  );
}
